import React, { useState } from 'react';
import { 
  FaBell, FaCheckDouble, FaTrashAlt, FaFilter, FaSearch, 
  FaSpinner, FaExclamationCircle, FaInfinity, FaCog 
} from 'react-icons/fa';
import NotificationItem from '../components/notifications/NotificationItem';
import useNotifications from '../hooks/useNotifications';
import { useToast } from '../components/ToastContainer';
import './NotificationsPage.css';

const NotificationsPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedNotifications, setSelectedNotifications] = useState(new Set());
  const { showSuccess, showError, showWarning } = useToast();

  const {
    notifications,
    unreadCount,
    loading,
    error,
    hasMore,
    refresh,
    loadMore,
    markAsRead,
    markAllAsRead,
    deleteNotification
  } = useNotifications();

  // Filtrer les notifications
  const filteredNotifications = notifications.filter(notif => {
    // Filtre par statut
    if (selectedFilter === 'unread' && notif.is_read) return false;
    if (selectedFilter === 'read' && !notif.is_read) return false;
    if (selectedFilter === 'today') {
      const today = new Date().toDateString();
      const notifDate = new Date(notif.created_at).toDateString();
      if (today !== notifDate) return false;
    }

    // Filtre par recherche
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        notif.title?.toLowerCase().includes(searchLower) ||
        notif.message?.toLowerCase().includes(searchLower)
      );
    }

    return true;
  });

  // Actions
  const handleMarkAsRead = async (notificationId) => {
    try {
      await markAsRead(notificationId);
      showSuccess('Notification marquée comme lue');
    } catch (error) {
      showError('Erreur lors du marquage');
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await deleteNotification(notificationId);
      showSuccess('Notification supprimée');
      setSelectedNotifications(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    } catch (error) {
      showError('Erreur lors de la suppression');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      showSuccess('Toutes les notifications marquées comme lues');
    } catch (error) {
      showError('Erreur lors du marquage global');
    }
  };

  const handleSelectNotification = (notificationId) => {
    setSelectedNotifications(prev => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedNotifications.size === filteredNotifications.length) {
      setSelectedNotifications(new Set());
    } else {
      setSelectedNotifications(new Set(filteredNotifications.map(n => n.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedNotifications.size === 0) return;

    try {
      await Promise.all(
        Array.from(selectedNotifications).map(id => deleteNotification(id))
      );
      showSuccess(`${selectedNotifications.size} notifications supprimées`);
      setSelectedNotifications(new Set());
    } catch (error) {
      showError('Erreur lors de la suppression groupée');
    }
  };

  const getFilterStats = () => {
    return {
      all: notifications.length,
      unread: unreadCount,
      read: notifications.length - unreadCount,
      today: notifications.filter(n => {
        const today = new Date().toDateString();
        const notifDate = new Date(n.created_at).toDateString();
        return today === notifDate;
      }).length
    };
  };

  const stats = getFilterStats();

  return (
    <div className="notifications-page">
      {/* En-tête */}
      <div className="page-header">
        <div className="header-left">
          <FaBell className="page-icon" />
          <div>
            <h1>Centre de Notifications</h1>
            <p>Gérez toutes vos notifications en un seul endroit</p>
          </div>
        </div>
        
        <div className="header-actions">
          <button
            className="btn-secondary"
            onClick={refresh}
            disabled={loading}
          >
            <FaSpinner className={loading ? 'spinning' : ''} />
            Actualiser
          </button>
          
          {unreadCount > 0 && (
            <button
              className="btn-primary"
              onClick={handleMarkAllAsRead}
            >
              <FaCheckDouble />
              Tout marquer comme lu ({unreadCount})
            </button>
          )}
        </div>
      </div>

      {/* Statistiques */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-value">{stats.all}</div>
          <div className="stat-label">Total</div>
        </div>
        <div className="stat-card unread">
          <div className="stat-value">{stats.unread}</div>
          <div className="stat-label">Non lues</div>
        </div>
        <div className="stat-card read">
          <div className="stat-value">{stats.read}</div>
          <div className="stat-label">Lues</div>
        </div>
        <div className="stat-card today">
          <div className="stat-value">{stats.today}</div>
          <div className="stat-label">Aujourd'hui</div>
        </div>
      </div>

      {/* Filtres et recherche */}
      <div className="filters-section">
        <div className="search-container">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Rechercher dans les notifications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-buttons">
          {[
            { key: 'all', label: `Toutes (${stats.all})` },
            { key: 'unread', label: `Non lues (${stats.unread})` },
            { key: 'read', label: `Lues (${stats.read})` },
            { key: 'today', label: `Aujourd'hui (${stats.today})` }
          ].map(filter => (
            <button
              key={filter.key}
              className={`filter-btn ${selectedFilter === filter.key ? 'active' : ''}`}
              onClick={() => setSelectedFilter(filter.key)}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Actions groupées */}
      {selectedNotifications.size > 0 && (
        <div className="bulk-actions">
          <div className="bulk-info">
            {selectedNotifications.size} notification(s) sélectionnée(s)
          </div>
          <div className="bulk-buttons">
            <button
              className="btn-danger"
              onClick={handleBulkDelete}
            >
              <FaTrashAlt />
              Supprimer
            </button>
          </div>
        </div>
      )}

      {/* Liste des notifications */}
      <div className="notifications-content">
        {error && (
          <div className="error-message">
            <FaExclamationCircle />
            <p>{error}</p>
            <button onClick={refresh}>Réessayer</button>
          </div>
        )}

        {!error && filteredNotifications.length === 0 && !loading && (
          <div className="empty-state">
            <FaBell className="empty-icon" />
            <h3>Aucune notification</h3>
            <p>
              {searchTerm && 'Aucune notification ne correspond à votre recherche.'}
              {!searchTerm && selectedFilter === 'unread' && 'Toutes vos notifications ont été lues !'}
              {!searchTerm && selectedFilter === 'today' && 'Aucune notification aujourd\'hui.'}
              {!searchTerm && selectedFilter === 'all' && 'Vous n\'avez encore aucune notification.'}
              {!searchTerm && selectedFilter === 'read' && 'Aucune notification lue.'}
            </p>
          </div>
        )}

        {filteredNotifications.length > 0 && (
          <div className="notifications-list">
            {/* En-tête de sélection */}
            <div className="selection-header">
              <label className="checkbox-container">
                <input
                  type="checkbox"
                  checked={selectedNotifications.size === filteredNotifications.length}
                  onChange={handleSelectAll}
                />
                Tout sélectionner
              </label>
            </div>

            {/* Liste */}
            {filteredNotifications.map(notification => (
              <div key={notification.id} className="notification-row">
                <label className="checkbox-container">
                  <input
                    type="checkbox"
                    checked={selectedNotifications.has(notification.id)}
                    onChange={() => handleSelectNotification(notification.id)}
                  />
                </label>
                
                <NotificationItem
                  notification={notification}
                  onMarkAsRead={handleMarkAsRead}
                  onDelete={handleDelete}
                  onClick={(notif) => {
                    console.log('Navigation to:', notif);
                  }}
                />
              </div>
            ))}

            {/* Charger plus */}
            {hasMore && (
              <div className="load-more-container">
                <button
                  className="load-more-btn"
                  onClick={loadMore}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <FaSpinner className="spinning" />
                      Chargement...
                    </>
                  ) : (
                    <>
                      <FaInfinity />
                      Charger plus
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;